import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Ban, Loader2, ChevronDown } from 'lucide-react';

export default function GenericSchedulerView({
  queries,
  onUpdateSchedule,
  onClearSchedule,
}) {
  const [selectedQueryId, setSelectedQueryId] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [interval, setInterval] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const showIntervalInput = useMemo(
    () => frequency === 'hourly' || frequency === 'minutes',
    [frequency]
  );

  useEffect(() => {
    const selectedQuery = queries.find((q) => q.id === selectedQueryId);
    if (selectedQuery) {
      setFrequency(selectedQuery.schedule_type || 'daily');
      setInterval(selectedQuery.frequency_interval || '');
    } else {
      setFrequency('daily');
      setInterval('');
    }
  }, [selectedQueryId, queries]);

  const handleUpdateSchedule = async () => {
    if (!selectedQueryId) return;
    if (
      showIntervalInput &&
      (!interval || isNaN(parseInt(interval, 10)) || parseInt(interval, 10) <= 0)
    ) {
      setError('Please enter a valid positive number for the interval.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');
    try {
      const intervalValue = showIntervalInput ? parseInt(interval, 10) : null;
      await onUpdateSchedule(selectedQueryId, frequency, intervalValue);
      setSuccessMessage('Schedule updated.');
    } catch (err) {
      setError(err.message || 'Failed to update schedule.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearSchedule = async () => {
    if (!selectedQueryId) return;
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');
    try {
      await onClearSchedule(selectedQueryId);
      setFrequency('daily');
      setInterval('');
      setSuccessMessage('Schedule cleared.');
    } catch (err) {
      setError(err.message || 'Failed to clear schedule.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="bg-red-900/50 text-red-300 p-3 rounded-lg mb-4 border border-red-700">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-900/50 text-green-300 p-3 rounded-lg mb-4 border border-green-700">
          {successMessage}
        </div>
      )}
      <div className="bg-gray-900/50 p-6 rounded-lg mb-8">
        <h3 className="text-xl font-semibold text-gray-300 mb-4">
          Update Query Schedule
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Select Query
            </label>
            <select
              value={selectedQueryId}
              onChange={(e) => setSelectedQueryId(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="" disabled>
                Select a query...
              </option>
              {queries.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.title}{' '}
                  {q.schedule_type
                    ? `(${q.schedule_type}${q.frequency_interval ? ` - ${q.frequency_interval}` : ''})`
                    : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 bottom-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Frequency
            </label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="hourly">Hourly</option>
              <option value="minutes">Minutes</option>
            </select>
          </div>
          <div className={showIntervalInput ? 'block' : 'hidden'}>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Interval ({frequency === 'hourly' ? 'Hours' : 'Minutes'}) *
            </label>
            <input
              type="number"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              min="1"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              required={showIntervalInput}
            />
          </div>
        </div>
        <div className="flex gap-4 mt-4">
          <button
            onClick={handleUpdateSchedule}
            disabled={
              isLoading || !selectedQueryId || (showIntervalInput && !interval)
            }
            className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center"
          >
            {isLoading ? (
              <Loader2 className="animate-spin mr-2" />
            ) : (
              <Calendar size={18} className="mr-1" />
            )}{' '}
            Update Schedule
          </button>
          <button
            onClick={handleClearSchedule}
            disabled={
              isLoading ||
              !selectedQueryId ||
              !queries.find((q) => q.id === selectedQueryId)?.schedule_type
            }
            className="bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center"
          >
            {isLoading ? (
              <Loader2 className="animate-spin mr-2" />
            ) : (
              <Ban size={18} className="mr-1" />
            )}{' '}
            Clear Schedule
          </button>
        </div>
      </div>
    </div>
  );
}

